import React, { useEffect, useMemo, useRef, useState } from "react"
import Sortable from "sortablejs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Modal from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import IconButton from "@react/shared/ui/IconButton"
import { roundMoney } from "@react/helpers/helpers"
import QuillInput from "@shared/quillInput"
import { formatMoney } from "../helpers/poTotals"
import { fetchFormData, fetchCategoryFields, createPurchaseInvoice } from "../api/purchase_invoices"

const LABEL    = "block text-xs font-medium text-slate-700 mb-1.5"
const INPUT    = "w-full h-10 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400 transition"
const TEXTAREA = "w-full px-3 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400 transition"
const SELECT   = `${INPUT} bg-white`
const t = (key, opts) => I18n.t(`projects.expenses.invoices.form.${key}`, opts)
const Required = () => <span className="text-rose-500"> *</span>
const round2 = (value) => Math.round((parseFloat(value) || 0) * 100) / 100
const plainText = (html) => new DOMParser().parseFromString(html || "", "text/html").body.textContent.trim()

const ANSWER_KEY = {
  string: "string_answer", options: "string_answer", link_url: "string_answer",
  date: "date_answer", integer: "integer_answer", float: "float_answer", boolean: "boolean_answer",
}

const PickerField = ({ value, placeholder, staticOptions, onSelect }) => {
  const [query, setQuery]     = useState("")
  const [open, setOpen]       = useState(false)
  const [results, setResults] = useState([])
  const wrapRef = useRef(null)

  useEffect(() => {
    const onDocClick = (event) => { if (!wrapRef.current?.contains(event.target)) { setOpen(false); setQuery("") } }
    document.addEventListener("click", onDocClick, true)
    return () => document.removeEventListener("click", onDocClick, true)
  }, [])

  const handleInput = (event) => {
    const next = event.target.value
    setQuery(next); setOpen(true)
    const needle = next.toLowerCase()
    setResults((staticOptions || []).filter((option) => (option.name || "").toLowerCase().includes(needle)).slice(0, 50))
  }
  const handleFocus = () => { setOpen(true); setQuery(""); setResults((staticOptions || []).slice(0, 50)) }
  const select = (option) => { onSelect(option); setQuery(""); setOpen(false) }

  return (
    <div className="relative" ref={wrapRef}>
      <input className={INPUT} value={open ? query : (value?.name || "")} onChange={handleInput} onFocus={handleFocus} placeholder={placeholder} />
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400">{t("no_match")}</div>
          ) : results.map((option) => (
            <button key={option.id} type="button" onClick={() => select(option)} className="w-full text-left px-3 py-2 text-sm text-slate-800 hover:bg-slate-50">
              {option.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const PropertyFieldInput = ({ field, value, onChange }) => {
  if (field.field_type === "sub_title") {
    return <div className="mt-1 text-[13px] font-semibold text-slate-800">{field.title}</div>
  }
  const set = (next) => onChange(field.property_field_blueprint_id, next)
  return (
    <div>
      {field.field_type !== "boolean" && <label className={LABEL}>{field.title}{field.required && <Required />}</label>}
      {field.field_type === "string"  && <textarea className={TEXTAREA} rows={2} value={value || ""} onChange={(event) => set(event.target.value)} />}
      {field.field_type === "date"    && <input type="date" className={INPUT} value={value || ""} onChange={(event) => set(event.target.value)} />}
      {field.field_type === "integer" && <input type="number" step="1" className={INPUT} value={value || ""} onChange={(event) => set(event.target.value)} />}
      {field.field_type === "float"   && <input type="number" step="0.01" className={INPUT} value={value || ""} onChange={(event) => set(event.target.value)} />}
      {field.field_type === "link_url" && <input type="url" className={INPUT} value={value || ""} onChange={(event) => set(event.target.value)} />}
      {field.field_type === "options" && (
        <select className={SELECT} value={value || ""} onChange={(event) => set(event.target.value)}>
          <option value="">—</option>
          {(field.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      )}
      {field.field_type === "boolean" && (
        <label className="flex items-center gap-2 text-[12.5px] text-slate-700 mt-5">
          <input type="checkbox" className="h-4 w-4 rounded border border-slate-300" checked={!!value} onChange={(event) => set(event.target.checked)} />
          {field.title}{field.required && <Required />}
        </label>
      )}
    </div>
  )
}

const PurchaseInvoiceModal = ({ isOpen, projectId, purchaseOrderId, onClose, onSaved }) => {
  const keyRef = useRef(0)
  const nextKey = () => { keyRef.current += 1; return `v${keyRef.current}` }

  const [formData, setFormData] = useState(null)
  const [loading, setLoading]   = useState(false)
  const [saving, setSaving]     = useState(false)
  const [errors, setErrors]     = useState([])

  const [supplier, setSupplier] = useState(null)
  const [form, setForm]         = useState({})
  const [lines, setLines]       = useState([])
  const [document, setDocument] = useState(null)
  const [propertyFields, setPropertyFields] = useState([])
  const [propertyValues, setPropertyValues] = useState({})

  useEffect(() => {
    if (!isOpen) return
    setLoading(true); setErrors([]); setSupplier(null); setDocument(null); setPropertyValues({})
    fetchFormData(purchaseOrderId).then((data) => {
      setFormData(data)
      const prefill = data.prefill
      setForm({
        title: "", description: prefill?.description || "",
        issue_date: data.defaults?.issue_date || "", due_date: "",
        count_as_cost: "true", billing_category_id: "", category_id: "",
        currency: data.default_currency, purchase_order_id: prefill?.purchase_order_id || "",
      })
      setSupplier(prefill?.supplier || null)
      setLines((prefill?.lines || [{}]).map((line) => ({ key: nextKey(), description: line.description || "", subtotal: line.subtotal ?? "", vat_rate_id: line.vat_rate_id || "", billing_category_id: "" })))
    }).finally(() => setLoading(false))
    fetchCategoryFields(null).then((fields) => setPropertyFields(Array.isArray(fields) ? fields : []))
  }, [isOpen, purchaseOrderId])

  const vatRates     = formData?.vat_rates || []
  const vatRatesById = useMemo(() => Object.fromEntries(vatRates.map((rate) => [rate.id, rate])), [vatRates])
  const currency     = form.currency || formData?.default_currency
  const categoryOnLine = !!formData?.billing_category_on_line

  const totals = useMemo(() => {
    let subtotal = 0
    let vat = 0
    lines.forEach((line) => {
      const amount     = parseFloat(line.subtotal) || 0
      const percentage = vatRatesById[line.vat_rate_id]?.percentage || 0
      subtotal += amount
      vat += roundMoney(amount * percentage / 100)
    })
    subtotal = round2(subtotal)
    vat = round2(vat)
    return { subtotal, vat, total: round2(subtotal + vat) }
  }, [lines, vatRatesById])

  const setField = (key, value) => setForm((previous) => ({ ...previous, [key]: value }))

  const onCategoryChanged = (categoryId) => {
    setField("category_id", categoryId)
    fetchCategoryFields(categoryId || null).then((fields) => setPropertyFields(Array.isArray(fields) ? fields : []))
  }

  const updateLine = (key, patch) => setLines((previous) => previous.map((line) => (line.key === key ? { ...line, ...patch } : line)))
  const addLine    = () => setLines((previous) => [...previous, { key: nextKey(), description: "", subtotal: "", vat_rate_id: supplier?.vat_rate_id || "", billing_category_id: "" }])
  const removeLine = (key) => setLines((previous) => (previous.length <= 1 ? previous : previous.filter((line) => line.key !== key)))
  const moveLine   = (from, to) => setLines((previous) => {
    const next = [...previous]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    return next
  })

  const linesRef = useRef(null)
  useEffect(() => {
    if (!isOpen || loading || !linesRef.current) return
    const sortable = new Sortable(linesRef.current, {
      handle: ".sortable-handle", draggable: ".line-card", animation: 150, ghostClass: "opacity-40",
      onEnd: (event) => {
        const from = event.oldDraggableIndex ?? event.oldIndex
        const to   = event.newDraggableIndex ?? event.newIndex
        if (from !== to) moveLine(from, to)
      },
    })
    return () => sortable.destroy()
  }, [isOpen, loading, formData])

  const setPropertyValue = (blueprintId, value) => setPropertyValues((previous) => ({ ...previous, [blueprintId]: value }))

  const filledLines = () => lines.filter((line) => plainText(line.description) || line.subtotal || line.vat_rate_id || line.billing_category_id)

  const buildFormData = () => {
    const payload = new FormData()
    const put = (key, value) => payload.append(`purchase_invoice[${key}]`, value ?? "")
    put("title", form.title)
    put("description", form.description)
    put("issue_date", form.issue_date)
    put("due_date", form.due_date)
    put("count_as_cost", form.count_as_cost)
    put("supplier_id", supplier?.id || "")
    put("purchase_order_id", form.purchase_order_id || "")
    if (!categoryOnLine) put("billing_category_id", form.billing_category_id || "")
    put("category_id", form.category_id || "")
    put("currency", currency || "")

    filledLines().forEach((line, index) => {
      const base = `purchase_invoice[lines_attributes][${index}]`
      payload.append(`${base}[description]`, line.description || "")
      payload.append(`${base}[subtotal]`, line.subtotal || 0)
      payload.append(`${base}[vat_rate_id]`, line.vat_rate_id || "")
      if (categoryOnLine) payload.append(`${base}[billing_category_id]`, line.billing_category_id || "")
      payload.append(`${base}[order]`, index)
    })

    // The cost lines are derived from the project, split per cost type when a
    // purchase order backs the invoice.
    put("project_id", projectId)

    propertyFields.filter((field) => field.field_type !== "sub_title").forEach((field, index) => {
      const base = `purchase_invoice[property_fields_attributes][${index}]`
      payload.append(`${base}[property_field_blueprint_id]`, field.property_field_blueprint_id)
      payload.append(`${base}[field_type]`, field.field_type)
      payload.append(`${base}[title]`, field.title)
      payload.append(`${base}[required]`, field.required)
      payload.append(`${base}[order]`, field.order ?? "")
      payload.append(`${base}[category_id]`, field.category_id ?? "")
      payload.append(`${base}[special_function]`, field.special_function ?? "")
      payload.append(`${base}[${ANSWER_KEY[field.field_type]}]`, propertyValues[field.property_field_blueprint_id] ?? "")
    })

    if (document) payload.append("purchase_invoice[document]", document)
    payload.append("commit", "Save")
    return payload
  }

  const handleSubmit = () => {
    const clientErrors = []
    if (!supplier?.id) clientErrors.push(t("errors.supplier_required"))
    if (!form.description?.trim()) clientErrors.push(t("errors.description_required"))
    if (!form.issue_date) clientErrors.push(t("errors.issue_date_required"))
    const filled = filledLines()
    if (!filled.length || filled.some((line) => !line.subtotal || !line.vat_rate_id)) clientErrors.push(t("errors.amount_required"))
    if (formData.billing_category_mandatory) {
      const missing = categoryOnLine ? filled.some((line) => !line.billing_category_id) : formData.categories_enabled && !form.billing_category_id
      if (missing) clientErrors.push(t("errors.billing_category_required"))
    }
    if (clientErrors.length) { setErrors(clientErrors); return }

    setSaving(true); setErrors([])
    createPurchaseInvoice(buildFormData())
      .then((response) => {
        if (response?.errors) { setErrors(response.errors); setSaving(false); return }
        onSaved(response); onClose()
      })
      .catch(() => { setErrors([t("errors.network")]); setSaving(false) })
  }

  const footer = (
    <>
      <div />
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="md" onClick={onClose} disabled={saving}>{t("cancel")}</Button>
        <Button variant="primary" size="md" onClick={handleSubmit} disabled={saving || loading}>
          {saving && <FontAwesomeIcon icon="circle-notch" spin className="mr-1.5" />}{t("save")}
        </Button>
      </div>
    </>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} eyebrow={t("eyebrow")} title={t("title")} size="lg" footer={footer}>
      {loading || !formData ? (
        <div className="py-16 text-center text-slate-400"><FontAwesomeIcon icon="circle-notch" spin className="text-2xl" /></div>
      ) : (
        <>
          <section className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <label className={LABEL}>{t("supplier")}<Required /></label>
              <PickerField value={supplier} placeholder={t("search_supplier")} staticOptions={formData.suppliers} onSelect={setSupplier} />
            </div>
            <div className="col-span-12 md:col-span-6">
              <label className={LABEL}>{t("invoice_title")}</label>
              <input className={INPUT} value={form.title} onChange={(event) => setField("title", event.target.value)} />
            </div>
            <div className="col-span-12">
              <label className={LABEL}>{t("description")}<Required /></label>
              <input className={INPUT} value={form.description} onChange={(event) => setField("description", event.target.value)} />
            </div>
            <div className="col-span-6 md:col-span-4">
              <label className={LABEL}>{t("issue_date")}<Required /></label>
              <input type="date" className={INPUT} value={form.issue_date} onChange={(event) => setField("issue_date", event.target.value)} />
            </div>
            <div className="col-span-6 md:col-span-4">
              <label className={LABEL}>{t("due_date")}</label>
              <input type="date" className={INPUT} value={form.due_date} onChange={(event) => setField("due_date", event.target.value)} />
            </div>
            <div className="col-span-12 md:col-span-4">
              <label className={LABEL}>{t("count_as_cost")}</label>
              <select className={SELECT} value={form.count_as_cost} onChange={(event) => setField("count_as_cost", event.target.value)}>
                {formData.count_as_cost_options.map((option) => <option key={String(option.value)} value={String(option.value)}>{option.label}</option>)}
              </select>
            </div>
          </section>

          <section className="border-t border-slate-100 pt-5">
            <div className="flex items-center justify-between mb-2.5">
              <h3 className="text-[13px] font-semibold text-slate-800">{t("amounts")}</h3>
              <Button variant="secondary" size="xs" onClick={addLine}><FontAwesomeIcon icon="plus" className="mr-1" />{t("add_line")}</Button>
            </div>
            <div ref={linesRef} className="space-y-2">
              {lines.map((line) => {
                const amount     = parseFloat(line.subtotal) || 0
                const percentage = vatRatesById[line.vat_rate_id]?.percentage || 0
                return (
                  <div key={line.key} className="line-card rounded-lg border border-slate-200 bg-white p-3 space-y-2">
                    <div className="tw-quill">
                      <QuillInput text={line.description} onChange={(content) => updateLine(line.key, { description: content })} />
                    </div>
                    <div className={`grid ${categoryOnLine ? "grid-cols-[28px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_120px_34px]" : "grid-cols-[28px_minmax(0,1fr)_minmax(0,1fr)_120px_34px]"} gap-2 items-center`}>
                      <span className="sortable-handle flex items-center justify-center h-10 cursor-grab text-slate-300 hover:text-slate-500" title={t("drag_handle")}>
                        <FontAwesomeIcon icon="grip-vertical" />
                      </span>
                      <input type="number" step="0.01" placeholder={t("subtotal")} className={`${INPUT} text-right tabular-nums`} value={line.subtotal} onChange={(event) => updateLine(line.key, { subtotal: event.target.value })} />
                      <select className={SELECT} value={line.vat_rate_id} onChange={(event) => updateLine(line.key, { vat_rate_id: event.target.value })}>
                        <option value="">{t("select_vat")}</option>
                        {vatRates.map((rate) => <option key={rate.id} value={rate.id}>{rate.label}</option>)}
                      </select>
                      {categoryOnLine && (
                        <select className={SELECT} value={line.billing_category_id} title={t("billing_category")} onChange={(event) => updateLine(line.key, { billing_category_id: event.target.value })}>
                          <option value="">{t("billing_category")}{formData.billing_category_mandatory ? " *" : ""}</option>
                          {formData.billing_categories.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
                        </select>
                      )}
                      <div className="text-right tabular-nums text-[13px] text-slate-700">{formatMoney(amount * (1 + percentage / 100), currency)}</div>
                      <IconButton tone="danger" size="sm" title={t("remove_row")} disabled={lines.length <= 1} onClick={() => removeLine(line.key)}>
                        <FontAwesomeIcon icon="minus" />
                      </IconButton>
                    </div>
                  </div>
                )
              })}
            </div>
          </section>

          {formData.categories_enabled && (
            <section className="border-t border-slate-100 pt-5">
              <div className="space-y-4">
                {formData.categories?.length > 0 && (
                  <div>
                    <label className={LABEL}>{t("category")}</label>
                    <select className={SELECT} value={form.category_id} onChange={(event) => onCategoryChanged(event.target.value)}>
                      <option value="">—</option>
                      {formData.categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                    </select>
                  </div>
                )}
                {propertyFields.map((field) => (
                  <PropertyFieldInput key={field.property_field_blueprint_id} field={field} value={propertyValues[field.property_field_blueprint_id]} onChange={setPropertyValue} />
                ))}
                {!categoryOnLine && (
                  <div>
                    <label className={LABEL}>{t("billing_category")}{formData.billing_category_mandatory && <Required />}</label>
                    <select className={SELECT} value={form.billing_category_id} onChange={(event) => setField("billing_category_id", event.target.value)}>
                      <option value="">—</option>
                      {formData.billing_categories.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </section>
          )}

          {formData.multi_currency && (
            <section className="border-t border-slate-100 pt-5">
              <label className={LABEL}>{t("currency")}</label>
              <select className={`${SELECT} md:w-64`} value={form.currency} onChange={(event) => setField("currency", event.target.value)}>
                {formData.currencies.map((entry) => <option key={entry.code} value={entry.code}>{entry.label}</option>)}
              </select>
            </section>
          )}

          <section className="border-t border-slate-100 pt-5">
            <label className={LABEL}>{t("document")}</label>
            <label className="flex items-center gap-3 px-3 py-3 rounded-md border border-dashed border-slate-300 cursor-pointer hover:border-brand-400 hover:bg-brand-50/40 transition">
              <FontAwesomeIcon icon="paperclip" className="text-slate-400" />
              <span className="text-[13px] text-slate-600">{document ? document.name : t("add_document")}</span>
              <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(event) => setDocument(event.target.files?.[0] || null)} />
            </label>
          </section>

          <section className="flex justify-end">
            <div className="w-full md:w-72 rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-1.5">
              <div className="flex items-center justify-between text-[13px]"><span className="text-slate-500">{t("subtotal_ht")}</span><span className="tabular-nums font-medium text-slate-900">{formatMoney(totals.subtotal, currency)}</span></div>
              <div className="flex items-center justify-between text-[12.5px]"><span className="text-slate-400">{t("vat")}</span><span className="tabular-nums text-slate-600">{formatMoney(totals.vat, currency)}</span></div>
              <div className="flex items-center justify-between text-[15px] pt-1.5 border-t border-slate-200"><span className="font-semibold text-slate-700">{t("total_ttc")}</span><span className="tabular-nums font-semibold text-slate-900">{formatMoney(totals.total, currency)}</span></div>
            </div>
          </section>

          {errors.length > 0 && (
            <div className="px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">{errors.join(", ")}</div>
          )}
        </>
      )}
    </Modal>
  )
}

export default PurchaseInvoiceModal
